import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    ViewChild,
    OnInit,
} from '@angular/core';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, BehaviorSubject } from 'rxjs';
import {
    filter,
    map,
    switchMap,
    take,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import { IAclID } from 'src/app/acl-data/aclID.interface';
import {
    Annotation,
    Document,
    INITIAL_DOCUMENT,
} from 'src/app/acl-data/document/document.dto';
import { DocumentService } from 'src/app/acl-data/document/document.service';
import { FolderService } from 'src/app/acl-data/folder/folder.service';
import { deepCopy } from 'src/app/acl-data/general/generic.function';
import { UserDataService } from 'src/app/acl-data/userData/user-data.service';
import { IBookmarkEntry } from 'src/app/navigation/menuInterfaces/bookmark-entry.interface';
import { TopnavInfoService } from 'src/app/navigation/menuInterfaces/topnav-info.service';
import { DocumentPropertiesEditComponent } from 'src/app/shared-controls/document-properties-edit/document-properties-edit.component';
import { ReasonDialogComponent } from 'src/app/shared-controls/reason-dialog/reason-dialog.component';
import { SubSink } from 'subsink';
import { ObjectStoreService } from 'src/app/acl-data/object-store/object-store.service';
import { MatSnackBar } from '@angular/material/snack-bar';

export enum EDIT_SELECTED_TAB {
    Properties = 0,
    LinkedDocuments = 1,
    Delete = 2,
    Security = 3,
}

@Component({
    templateUrl: './document-edit.component.html',
    styleUrls: ['./document-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentEditComponent implements OnDestroy, OnInit {
    subSink = new SubSink();
    saveFailed = false;
    saving = false;

    loading$ = new BehaviorSubject(false);

    ADDENDUM = 22;

    @ViewChild('properties') properties: DocumentPropertiesEditComponent;
    @ViewChild('ContractDeleteReason')
    ContractDeleteReason: any;
    @ViewChild('tabs', { static: true }) tabs: MatTabGroup;

    // public file: IFileData;
    public document: Document = deepCopy(INITIAL_DOCUMENT);

    documentID$ = this.route.paramMap.pipe(
        map(v => v.get('documentID')),
        withLatestFrom(this.route.data),

        map(([idValue, routeValue]) => {
            return {
                id: idValue,
                objectStore: routeValue.parentMenu.data.id,
            } as IAclID;
        })
    );

    document$ = this.documentID$.pipe(
        switchMap(v => this.documentService.getByID(v.objectStore, v.id, true))
    );

    folder$ = this.document$.pipe(
        withLatestFrom(this.documentID$),
        switchMap(([doc, id]) =>
            this.folderService.getByID(
                id.objectStore,
                doc.propertiesObj.FoldersFiledInID
            )
        )
    );

    userRoles$ = this.objectStoreService.entities$.pipe(
        withLatestFrom(this.documentID$),
        map(([v, ID]) => v.find(row => row.symbolicName === ID.objectStore))
    );

    currentTab$ = this.route.queryParams.pipe(
        map(v => v.currentTab),
        filter(v => !!v)
    );

    isDeleted = () => {
        return !!this.document.propertiesObj.ContractDeletedDate;
    };

    deletedToggle = () => {
        if (this.document.propertiesObj.ContractDeletedDate) {
            this.document.propertiesObj.ContractDeletedDate = null;
            this.document.propertiesObj.ContractDeleteReason = null;
        } else {
            this.document.propertiesObj.ContractDeletedDate = new Date();
        }
    };

    linkedDocumentRequired = () => {
        return (
            this.document &&
            this.document.propertiesObj.ContractAgreementType === this.ADDENDUM
        );
    };

    ngOnDestroy(): void {
        this.subSink.unsubscribe();
    }

    ngOnInit() {
        this.subSink.add(
            combineLatest([this.currentTab$, this.document$]).subscribe(
                ([v, _]) => {
                    console.log(v,_)
                    if (!!this.tabs) {
                        if (this.tabs.selectedIndex !== v) {
                            const tabValue = (EDIT_SELECTED_TAB[
                                v
                            ] as unknown) as number;
                            this.tabs.selectedIndex = tabValue;
                        }
                    }
                }
            )
        );
    }

    tabClicked = (event: MatTabChangeEvent) => {
        const selectIndex = EDIT_SELECTED_TAB[event.index];
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { currentTab: selectIndex },
            queryParamsHandling: 'merge',
        });
    };

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private folderService: FolderService,
        private documentService: DocumentService,
        private topNavInfoService: TopnavInfoService,
        private objectStoreService: ObjectStoreService,
        public dialog: MatDialog,
        public snackBar: MatSnackBar,
        private userService: UserDataService,
        private cdr: ChangeDetectorRef
    ) {
        this.subSink.add(
            this.documentService.failed$.subscribe(error => {
                this.saving = false;
                this.saveFailed = true;
                cdr.detectChanges();
            })
        );

        this.subSink.add(
            this.document$.subscribe(v => {
                this.document = deepCopy(v);
            })
        );

        this.subSink.add(
            combineLatest([
                this.route.paramMap,
                this.route.data,
                this.document$,
            ])
                .pipe(
                    switchMap(([parmMap, routeValue]) =>
                        this.documentService
                            .displayById(parmMap.get('documentID'))
                            .pipe(
                                // Document for Breadcrumb
                                filter(v => !!v),
                                switchMap(doc =>
                                    this.folderService
                                        .getByID(
                                            // Category For breadcrumb
                                            routeValue.parentMenu.data.id,
                                            doc.propertiesObj.FoldersFiledInID
                                        )
                                        .pipe(
                                            filter(v => !!v),
                                            switchMap(folderCategory =>
                                                this.folderService
                                                    .getByID(
                                                        // Library For breadcrumb
                                                        routeValue.parentMenu
                                                            .data.id,
                                                        folderCategory.parentID
                                                    )
                                                    .pipe(
                                                        filter(v => !!v),
                                                        tap(folderLibrary => {
                                                            const objectStore =
                                                                routeValue
                                                                    .parentMenu
                                                                    .data.id;
                                                            if (!doc) {
                                                                return;
                                                            }
                                                            const result: IBookmarkEntry[] = [];
                                                            result.push({
                                                                path: `/${objectStore}/browse/${folderLibrary.id}`,
                                                                description:
                                                                    folderLibrary
                                                                        .propertiesObj
                                                                        .FolderName,
                                                            });
                                                            this.topNavInfoService.addExtraBreadCrumbData(
                                                                result
                                                            );
                                                            result.push({
                                                                path: `/${objectStore}/browse/${folderCategory.id}`,
                                                                description:
                                                                    folderCategory
                                                                        .propertiesObj
                                                                        .FolderName,
                                                            });
                                                            this.topNavInfoService.addExtraBreadCrumbData(
                                                                result
                                                            );
                                                            result.push({
                                                                path: `/${objectStore}/browse/${doc.id}`,
                                                                description:
                                                                    doc
                                                                        .propertiesObj
                                                                        .DocumentTitle,
                                                            });
                                                            this.topNavInfoService.addExtraBreadCrumbData(
                                                                result
                                                            );
                                                        })
                                                    )
                                            )
                                        )
                                )
                            )
                    )
                )
                .subscribe(v => {
                    return;
                })
        );
    }

    save() {
        console.log('Saving', this.document);
        delete this.document.propertiesObj.ContractLegalCounselEmail
        this.properties.touchAll();
        if (this.ContractDeleteReason) {
            this.ContractDeleteReason.control.markAsTouched();
        }
        if (
            this.properties.isValid &&
            (!this.document.propertiesObj.ContractDeletedDate ||
                !!this.document.propertiesObj.ContractDeleteReason) &&
            (!this.linkedDocumentRequired() ||
                this.document.linkedDocuments.length > 0)
        ) {
            const dialogRef = this.dialog.open(ReasonDialogComponent, {
                width: '80%',
            });

            dialogRef.afterClosed().subscribe(result => {
                if (result) {
                    this.loading$.next(true);
                    combineLatest([
                        this.userService.userData$,
                        this.documentID$,
                    ])
                        .pipe(take(1))
                        .subscribe(([user, ID]) => {
                            console.log('Reason supplied, saving');
                            const newComment: Annotation = {
                                propertiesObj: {
                                    AnnotationText: result,
                                    AnnotationDate: new Date(),
                                    AnnotationUser: user.username,
                                },
                            };

                            this.document.annotations = [
                                newComment,
                                ...this.document.annotations,
                            ];

                            console.log('SAVING:', this.document);
                            this.saving = true;
                            this.document.objectStore = ID.objectStore;

                            this.documentService
                                .update(this.document, {})
                                .subscribe(
                                    newDoc => {
                                        console.log(
                                            'Trying to navigate to ',
                                            newDoc.id
                                        );

                                        this.router.navigate(
                                            [
                                                '/',
                                                ID.objectStore,
                                                'DocumentView',
                                                newDoc.id,
                                            ],
                                            {
                                                relativeTo: this.route,
                                                preserveQueryParams: true,
                                            }
                                        );
                                    },
                                    err => {
                                        this.snackBar.open(
                                            'Unable to update contract, please contact your administrator',
                                            'OK'
                                        );
                                        this.saving = false;
                                        this.loading$.next(false);
                                    },
                                    () => {
                                        this.saving = false;
                                        this.loading$.next(false);
                                    }
                                );
                        });
                }
            });
        } else {
            console.log('Form is not valid');

            if (
                this.document.propertiesObj.ContractDeletedDate &&
                !this.document.propertiesObj.ContractDeleteReason
            ) {
                // console.log('Deleted is invalid');
                this.tabs.selectedIndex = EDIT_SELECTED_TAB.Delete;
            }
            if (
                this.linkedDocumentRequired() &&
                this.document.linkedDocuments.length === 0
            ) {
                // console.log('Linked Documents is invalid');
                this.tabs.selectedIndex = EDIT_SELECTED_TAB.LinkedDocuments;
            }

            if (!this.properties.isValid) {
                // console.log('Properties is invalid', this.properties);
                this.tabs.selectedIndex = EDIT_SELECTED_TAB.Properties;
                this.properties.focusOnError();
            }
        }
    }
}
